import {
    IMetadataAction,
    IMetadataIntentAction,
    IMetadataMultiSelectAction,
    MetadataVerification,
    IMetadataDataCollectionAction,
    IMetadataResequenceAction,
    IMetadataAttachmentAction,
    IDocument,
} from "@tylertech/tyler-api-types";
import { ObjectHelpers } from "./ObjectHelpers";
import { Utilities } from "@tylertech/shared-module";
class MetadataHelpersImpl
{
    isAction(value: any): value is IMetadataAction
    {
        return (
            value &&
            typeof value === "object" &&
            (ObjectHelpers.hasField<IMetadataAction>("ViewToShow", value) ||
                ObjectHelpers.hasField<IMetadataAction>("ServicePath", value) ||
                ObjectHelpers.hasField<IMetadataAction>("FormToOpenInNewTab", value) ||
                ObjectHelpers.hasField<IMetadataAction>("RedirectToForm", value) ||
                this.isIntentAction(value))
        );
    }

    isIntentAction(value: any): value is IMetadataIntentAction
    {
        return (
            value &&
            typeof value === "object" &&
            ((ObjectHelpers.hasField<IMetadataIntentAction>("IntentMime", value) &&
                ObjectHelpers.hasField<IMetadataIntentAction>("IntentAction", value)) ||
                ObjectHelpers.hasField<IMetadataIntentAction>("IntentURI", value) ||
                ObjectHelpers.hasField<IMetadataIntentAction>("IsRefresh", value))
        );
    }

    isDataCollectionAction(value: any): value is IMetadataDataCollectionAction
    {
        return (
            value &&
            typeof value === "object" &&
            ObjectHelpers.hasField<IMetadataDataCollectionAction>("SendDataCollectionToService", value)
        );
    }

    isMultiSelectAction(value: any): value is IMetadataMultiSelectAction
    {
        return (
            value &&
            typeof value === "object" &&
            ObjectHelpers.hasField<IMetadataMultiSelectAction>("ContextKey", value)
        );
    }

    isResequenceAction(value: any): value is IMetadataResequenceAction
    {
        return (
            value &&
            typeof value === "object" &&
            ObjectHelpers.hasField<IMetadataResequenceAction>("SequenceProperty", value)
        );
    }

    isAttachmentAction(value: any): value is IMetadataAttachmentAction
    {
        return value && typeof value === "object" && this.isAction(value);
    }

    isMetadataVerification(value: any): value is MetadataVerification /* | MetadataCollectionVerification */
    {
        return (
            value &&
            typeof value === "object" &&
            ObjectHelpers.hasField<MetadataVerification>("IsValid", value) &&
            ObjectHelpers.hasField<MetadataVerification>("Type", value) &&
            (value.IsValid || ObjectHelpers.hasField<MetadataVerification>("FieldErrors", value))
        );
    }

    isIDocument(value: any): value is IDocument
    {
        return (
            value &&
            typeof value === "object" &&
            ObjectHelpers.hasField<IDocument>("Content", value) &&
            ObjectHelpers.hasField<IDocument>("MimeType", value) &&
            ObjectHelpers.hasField<IDocument>("Filename", value)
        );
    }

    GetDocumentsFromModel(model: any): IDocument[]
    {
        if (Utilities.IsNullOrUndefined(model)) return null;
        if (MetadataActionHelpers.isIDocument(model)) return [model];
        if (MetadataActionHelpers.isIDocument(model.Entity)) return [model.Entity];
        if (Array.isArray(model) && MetadataActionHelpers.isIDocument(model[0]))
            return model.map(
                g =>
                    <IDocument>{
                        Content: g.Content,
                        MimeType: g.MimeType,
                        Filename: g.Filename,
                    }
            );
        if (Array.isArray(model.Entity) && MetadataActionHelpers.isIDocument(model.Entity[0]))
            return model.Entity.map(
                g =>
                    <IDocument>{
                        Content: g.Content,
                        MimeType: g.MimeType,
                        Filename: g.Filename,
                    }
            );
        return null;
    }

    /**
     * Tests whether the `action` has a ServicePath value.
     *
     * Returns `false` if the `action`'s ServicePath is "NoServiceCall/".
     * @param action IMetadataAction to inspect
     */
    hasCustomServicePath(action: IMetadataAction): boolean
    {
        return action.ServicePath && !this.isReservedServicePath(action);
    }

    /**
     * Tests if the `action`'s service path is a reserved value; i.e., "NoServiceCall/" or "RequireValid/".
     * @param action IMetadataAction
     */
    isReservedServicePath(action: IMetadataAction): boolean
    {
        return this.isNoServicePath(action) || this.isRequiredValid(action);
    }

    /**
     * Tests if the `action`'s service path is the reserved value "NoServiceCall/".
     * @param action IMetadataAction
     */
    isNoServicePath(action: IMetadataAction): boolean
    {
        return /NoServiceCall\//.test(action.ServicePath);
    }

    /**
     * Tests if the `action`'s service path is the reserved value "RequireValid/".
     * @param action IMetadataAction
     */
    isRequiredValid(action: IMetadataAction): boolean
    {
        return /RequireValid\//.test(action.ServicePath);
    }

    /**
     * Tests if the `action`'s service path is the reserved value "RepositorySaveAll/".
     * @param action IMetadataAction
     */
    isRepositorySaveAll(action: IMetadataAction): boolean
    {
        return /RepositorySaveAll\//.test(action.ServicePath);
    }

    /**
     * Tests if the `action`'s return path is the reserved value "this".
     * @param action IMetadataAction
     */
    isReturnPathThis(action: IMetadataAction): boolean
    {
        return /this/.test(action.ReturnPath);
    }

    /**
     * Tests if the `action`'s return path is the reserved value "IDocument".
     * @param action IMetadataAction
     */
    isReturnPathDocument(action: IMetadataAction): boolean
    {
        return /IDocument/.test(action.ReturnPath);
    }

    isReturnPathResults(action: IMetadataAction): boolean
    {
        return /RESULT/.test(action.ReturnPath);
    }

    isReturnPathBack(action: IMetadataAction): boolean
    {
        return /BACK/.test(action.ReturnPath);
    }

    isReturnPathReload(action: IMetadataAction): boolean
    {
        return /RELOAD/.test(action.ReturnPath);
    }

    /**
     * Tests if the `action`'s model path is the reserved value "this".
     * @param action IMetadataAction
     */
    isModelPathThis(action: IMetadataAction): boolean
    {
        return /this/.test(action.ModelPath);
    }

    /**
     * Tests if the `action`'s model path is the reserved value "NEW_OBJECT".
     * @param action IMetadataAction
     */
    isModelPathNewObject(action: IMetadataAction): boolean
    {
        return /NEW_OBJECT/.test(action.ModelPath);
    }

    isModelPathRepositoryNew(action: IMetadataAction): boolean
    {
        return /RepositoryNew/.test(action.ModelPath);
    }

    isModelPathOtherEntities(action: IMetadataAction): boolean
    {
        return this.isOtherEntities(action.ModelPath);
    }

    isOtherEntities(value: string): boolean
    {
        return /OtherEntities\..+/.test(value);
    }

    isModelPathParent(action: IMetadataAction): boolean
    {
        return /Parent/.test(action.ModelPath);
    }

    isModelPathTabbedView(action: IMetadataAction): boolean
    {
        return /TabbedView/.test(action.ModelPath);
    }

    isFormViewBack(action: IMetadataAction): boolean
    {
        return /NavigateBack/.test(action.FormViewToShow);
    }

    IsCancelObject(result: any): boolean
    {
        return (
            !Utilities.IsNullOrUndefined(result) &&
            !(result !== Object(result)) &&
            "canceledActionDialog" in result &&
            result.canceledActionDialog === true
        );
    }
}
export const MetadataActionHelpers = new MetadataHelpersImpl();
